import React from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import CountUp from "react-countup";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ArrowRight, Megaphone, MessageSquare, Map, BookOpen, Users, Vote, Sparkles, Calendar, Heart, Mic, FileText, Globe, Star } from "lucide-react";
import SocialFeed from "@/components/site/SocialFeed";
import WhatsAppCard from "@/components/site/WhatsAppCard";
import PageBlocks from "@/components/site/PageBlocks";
import JoinCefBlock from "@/components/site/JoinCefBlock";
import LegalHelpBlock from "@/components/site/LegalHelpBlock";
import InlineFwbMap from "@/components/site/InlineFwbMap";
import EditableText from "@/components/site/EditableText";
import { renderHighlightedTitle } from "@/components/site/PageHeader";

const QUICK_ICONS = { Map, BookOpen, Vote, Megaphone, MessageSquare, Users, Sparkles, Calendar, Heart, Mic, FileText, Globe, Star };
const QUICK_COLORS = {
  pink: "bg-cef-pink text-white",
  blue: "bg-cef-blue text-white",
  yellow: "bg-cef-yellow",
  green: "bg-cef-green",
  white: "bg-white",
  black: "bg-black text-white",
};

const CATS_BADGES = {
  "Réformes de l'enseignement": "bg-cef-blue text-white",
  "Mobilisations": "bg-cef-pink text-white",
  "Vie scolaire": "bg-cef-yellow",
  "Bien-être": "bg-cef-green",
  "Numérique": "bg-black text-white",
  "Harcèlement": "bg-cef-pink text-white",
  "Orientation": "bg-cef-yellow",
  "Participation des élèves": "bg-cef-blue text-white",
};

function Stat({ value, label, color = "bg-cef-yellow", testId }) {
  const numeric = Number(value || 0);
  return (
    <div className={`p-5 sm:p-6 border-2 border-black rounded-md shadow-brutal ${color}`} data-testid={testId}>
      <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter tabular-nums">
        <CountUp end={numeric} duration={2.4} separator=" " enableScrollSpy scrollSpyOnce />
      </div>
      <div className="mt-1 text-sm font-bold uppercase tracking-tight">{label}</div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = React.useState(null);
  const [featured, setFeatured] = React.useState([]);
  const [actions, setActions] = React.useState([]);
  const [consult, setConsult] = React.useState(null);
  const [config, setConfig] = React.useState(null);
  const [showInlineMap, setShowInlineMap] = React.useState(false);
  const inlineMapRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/articles", { params: { featured: true, limit: 3 } }).then((r) => setFeatured(r.data));
    api.get("/actions").then((r) => setActions((r.data || []).filter((a) => a.status !== "termine").slice(0, 4)));
    api.get("/site-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  // Fetch consultation: either a specific slug picked in admin, or the latest open one
  const consultSlugOverride = config?.consultation_block?.consultation_slug || "";
  React.useEffect(() => {
    if (consultSlugOverride) {
      api.get(`/consultations/${consultSlugOverride}`).then((r) => setConsult(r.data?.consultation || r.data)).catch(() => setConsult(null));
    } else if (config) {
      api.get("/consultations", { params: { status_filter: "en_cours" } }).then((r) => setConsult(r.data[0])).catch(() => {});
    }
  }, [consultSlugOverride, config]);

  const banner = config?.banner || {};
  const sections = config?.home_sections || {};
  const show = (key) => sections[key] !== false; // default → visible
  const cb = config?.consultation_block || {};

  return (
    <div className="bg-cef-paper">
      {/* HERO (banner editable) */}
      {banner.enabled !== false && (
        <section className="relative overflow-hidden border-b-2 border-black">
          <div className="absolute inset-0 pattern-grid opacity-60" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="chip bg-cef-yellow mb-5 animate-wiggle" data-testid="hero-tag">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Plateforme des élèves · FWB
                </div>
                <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter uppercase leading-[1.05]" data-testid="hero-title">
                  {renderHighlightedTitle(banner.title) || "L'école nous concerne. Faisons entendre notre voix."}
                </h1>
                <p className="mt-6 text-base sm:text-lg max-w-2xl leading-relaxed text-neutral-700" data-testid="hero-subtitle">
                  {banner.subtitle || "Actualités, infos, consultations et actions portées par les élèves de toute la Fédération Wallonie-Bruxelles."}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to={banner.cta_link || "/rejoindre"} className="neo-btn bg-cef-pink text-white" data-testid="hero-cta">
                    <Megaphone className="w-4 h-4" /> {banner.cta_label || "Rejoindre le mouvement"}
                  </Link>
                  <Link to="/donner-mon-avis" className="neo-btn bg-cef-yellow" data-testid="hero-avis-btn">
                    <MessageSquare className="w-4 h-4" /> Donner mon avis
                  </Link>
                  <Link to="/carte" className="neo-btn bg-cef-green" data-testid="hero-actions-btn">
                    <Map className="w-4 h-4" /> Voir toutes les actions
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                {banner.image_url && (
                  <div className="relative">
                    <img src={banner.image_url} alt="Bannière" className="w-full max-w-md mx-auto border-2 border-black rounded-md shadow-brutalLg bg-white" data-testid="hero-image" />
                    <div className="absolute -top-3 -left-3 chip bg-cef-green animate-wiggle">100 % élèves</div>
                    <div className="absolute -bottom-3 -right-3 chip bg-cef-yellow">+{stats?.schools ?? 187} écoles</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MARQUEE */}
      <div className="bg-cef-yellow border-y-2 border-black overflow-hidden">
        <Marquee gradient={false} speed={45} className="py-3">
          {(config?.marquee && config.marquee.length > 0
            ? config.marquee
            : [
                "🔥 Plateforme officielle des élèves francophones",
                "📣 Consultations en cours · vote tes idées",
                "✊ +4000 élèves engagé·e·s dans toute la FWB",
                "🎤 Témoigne anonymement · partage ton vécu",
              ]
          ).map((m, i) => (
            <span key={`marquee-${i}`} className="mx-8 font-display font-black uppercase tracking-tight text-lg">{m}</span>
          ))}
        </Marquee>
      </div>

      {/* STATS */}
      {show("stats") && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stats-grid">
            <Stat value={stats?.students ?? 4280} label="Élèves engagé·e·s" color="bg-cef-blue text-white" testId="stat-students" />
            <Stat value={stats?.schools ?? 187} label="Écoles touchées" color="bg-cef-yellow" testId="stat-schools" />
            <Stat value={stats?.voiced ?? 0} label="Élèves ayant donné leur avis" color="bg-cef-pink text-white" testId="stat-voiced" />
          </div>
        </section>
      )}

      {/* CONSULTATION */}
      {show("consultation") && consult && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            <div className="md:col-span-2 neo-card p-6 sm:p-8 bg-cef-blue text-white" data-testid="consult-of-moment">
              <div className="chip bg-cef-yellow text-black mb-3">Consultation du moment</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter leading-tight">{cb.custom_title || consult.title}</h2>
              <p className="mt-3 text-white/90">{cb.custom_description || consult.description}</p>
              <div className="mt-5 flex flex-wrap gap-3 items-center">
                {cb.custom_cta_link ? (
                  cb.custom_cta_link.startsWith("http") ? (
                    <a href={cb.custom_cta_link} target="_blank" rel="noreferrer" className="neo-btn bg-white text-black" data-testid="consult-of-moment-cta">
                      {cb.custom_cta_label || "Donner mon avis"} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link to={cb.custom_cta_link} className="neo-btn bg-white text-black" data-testid="consult-of-moment-cta">
                      {cb.custom_cta_label || "Donner mon avis"} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )
                ) : consult.external_url ? (
                  <a href={consult.external_url} target="_blank" rel="noreferrer" className="neo-btn bg-white text-black" data-testid="consult-of-moment-cta">
                    {cb.custom_cta_label || "Donner mon avis"} <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <Link to={`/donner-mon-avis/${consult.slug}`} className="neo-btn bg-white text-black" data-testid="consult-of-moment-cta">
                    {cb.custom_cta_label || "Donner mon avis"} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                <div className="text-sm font-bold">{(consult.respondents_override !== null && consult.respondents_override !== undefined) ? consult.respondents_override : consult.participants_count} participant·e·s</div>
              </div>
            </div>
            <div className="neo-card p-6 bg-cef-yellow">
              <div className="font-display font-black text-xl tracking-tighter uppercase">Tu veux agir ?</div>
              <p className="mt-2 text-sm">Rejoins un groupe local, propose une action, deviens membre du CEF.</p>
              <Link to="/agir" className="neo-btn bg-black text-white mt-4 w-full justify-center" data-testid="home-agir-cta">
                <Users className="w-4 h-4" /> Passer à l'action
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ACTIONS À VENIR — moved ABOVE "À la une" */}
      {show("actions_upcoming") && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display font-black text-2xl sm:text-4xl tracking-tighter uppercase flex items-center gap-2">
            <Calendar className="w-7 h-7" strokeWidth={2.5} /> Actions à venir
          </h2>
          <Link to="/agir" className="text-sm font-bold hover:underline" data-testid="all-actions-link">Tout voir →</Link>
        </div>
        {actions.length === 0 ? (
          <div className="neo-card p-6 bg-white text-neutral-500" data-testid="actions-empty">
            Aucune action programmée pour le moment. <Link to="/carte" className="font-bold underline">Lance la tienne sur la carte ↗</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="actions-upcoming">
            {actions.map((a) => (
              <Link key={a.id} to={`/actions/${a.id}`} className="neo-card p-5 bg-white block group hover:-translate-y-0.5 transition" data-testid={`upcoming-action-${a.id}`}>
                <span className={`chip ${a.type === "mobilisation" ? "bg-cef-pink text-white" : a.type === "outil" ? "bg-cef-yellow" : "bg-cef-blue text-white"}`}>{a.type}</span>
                <h3 className="mt-3 font-display font-black text-lg tracking-tight leading-snug group-hover:underline">{a.title}</h3>
                <div className="mt-2 text-xs text-neutral-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.date}</div>
                <p className="mt-2 text-sm text-neutral-700 line-clamp-2">{a.description}</p>
                <span className="mt-3 inline-flex text-xs font-bold underline">Voir l'action →</span>
              </Link>
            ))}
          </div>
        )}
      </section>
      )}

      {/* FEATURED — À LA UNE (style social/media feed) */}
      {show("featured_articles") && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display font-black text-2xl sm:text-4xl tracking-tighter uppercase">À la une</h2>
          <Link to="/actualites" className="text-sm font-bold hover:underline" data-testid="all-news-link">Tout voir →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5" data-testid="featured-grid">
          {featured.map((a) => (
            <Link key={a.id} to={`/actualites/${a.slug}`} className="neo-card overflow-hidden block group bg-white" data-testid={`featured-article-${a.slug}`}>
              {a.image_url ? (
                <div className="aspect-[4/5] overflow-hidden border-b-2 border-black bg-cef-paper">
                  <img src={a.image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                </div>
              ) : (
                <div className="aspect-[4/5] bg-cef-yellow border-b-2 border-black flex items-center justify-center font-display font-black text-2xl px-6 text-center">{a.title}</div>
              )}
              <div className="p-4">
                <span className={`chip ${CATS_BADGES[a.category] || "bg-white"}`}>{a.category}</span>
                <h3 className="mt-2 font-display font-black text-base tracking-tight leading-snug line-clamp-2">{a.title}</h3>
                <p className="mt-1.5 text-xs text-neutral-600 line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* QUICK ACCESS — dynamic from site_config */}
      {show("quick_access") && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-access-grid">
          {(config?.quick_access || []).map((item, idx) => {
            const Icon = QUICK_ICONS[item.icon] || Sparkles;
            const colorClass = QUICK_COLORS[item.color] || "bg-white";
            const isExternal = item.link?.startsWith("http");
            const isFwbMap = !isExternal && item.link === "/carte";
            if (isFwbMap) {
              return (
                <button
                  key={`${item.label}-${item.link}-${idx}`}
                  type="button"
                  onClick={() => {
                    setShowInlineMap(true);
                    setTimeout(() => inlineMapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
                  }}
                  className={`neo-card p-5 ${colorClass} flex flex-col items-start gap-2 text-left`}
                  data-testid={`quick-${idx}`}
                  aria-expanded={showInlineMap}
                >
                  <Icon className="w-6 h-6" strokeWidth={2.5} />
                  <span className="font-display font-black text-lg tracking-tight">{item.label}</span>
                  <span className="text-xs font-bold uppercase tracking-widest opacity-70 mt-auto">{showInlineMap ? "Replier" : "Aperçu rapide"}</span>
                </button>
              );
            }
            return isExternal ? (
              <a key={`${item.label}-${item.link}-${idx}`} href={item.link} target="_blank" rel="noopener noreferrer" className={`neo-card p-5 ${colorClass} flex flex-col items-start gap-2`} data-testid={`quick-${idx}`}>
                <Icon className="w-6 h-6" strokeWidth={2.5} />
                <span className="font-display font-black text-lg tracking-tight">{item.label}</span>
              </a>
            ) : (
              <Link key={`${item.label}-${item.link}-${idx}`} to={item.link || "/"} className={`neo-card p-5 ${colorClass} flex flex-col items-start gap-2`} data-testid={`quick-${idx}`}>
                <Icon className="w-6 h-6" strokeWidth={2.5} />
                <span className="font-display font-black text-lg tracking-tight">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </section>
      )}

      {/* INLINE FWB MAP — appears when "Carte FWB" tile is clicked */}
      {showInlineMap && (
        <div ref={inlineMapRef}>
          <InlineFwbMap onClose={() => setShowInlineMap(false)} />
        </div>
      )}

      {/* SOCIAL FEED */}
      {show("social_feed") && <SocialFeed />}

      {/* ADMIN-EDITABLE BLOCKS */}
      {show("page_blocks") && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-2">
          <PageBlocks slug="home" />
        </section>
      )}

      {/* LEGAL HELP — partner orgs (SDJ, InforJeunes, …) */}
      {show("legal_help") && <LegalHelpBlock data={config?.legal_help} />}

      {/* WHATSAPP — réservé aux élèves connectés */}
      {show("whatsapp") && user && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
          <WhatsAppCard />
        </section>
      )}

      {/* JOIN BANNER */}
      {show("join_banner") && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="neo-card bg-black text-white p-8 sm:p-12 text-center">
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter uppercase">Ton école te concerne. <span className="text-cef-yellow">Agis avec nous.</span></h2>
            <EditableText
              slug="home"
              sectionKey="bottom-cta"
              className="mt-3 text-white/80 max-w-2xl mx-auto [&_p]:m-0"
            >
              <p>Le CEF, c'est des centaines d'élèves de toute la Fédération Wallonie-Bruxelles. Rejoins-nous, propose une initiative, suis-nous sur les réseaux.</p>
            </EditableText>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/signup" className="neo-btn bg-cef-yellow text-black" data-testid="bottom-join-btn">Créer mon compte</Link>
              <Link to="/cef" className="neo-btn bg-white text-black" data-testid="bottom-cef-btn">Découvrir le CEF</Link>
            </div>
          </div>
        </section>
      )}

      {show("join_cef_block") && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <JoinCefBlock variant="yellow" />
        </section>
      )}
    </div>
  );
}
